import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { BODY_MAX, buildPostPayload, isDirty, postStatus, TITLE_MAX } from '../../lib/changelog.js';
import { css, html, nothing } from '../../lib/lit.js';

// Write or edit one changelog post: a product, a title and a Markdown body.
//
// Publishing is a separate step from saving, because it is the moment the post
// goes public. It is only offered on a saved post with no unsaved changes, and
// asks once more inline (Bootstrap's modal JS cannot reach into a shadow root).
class ChangelogEditorView extends BaseComponent {
    static properties = {
        /** The post being edited; absent for a new post. */
        postId: { type: String, attribute: 'post-id' },
        _loading: { state: true },
        _loadError: { state: true },
        /** @type {Array<{slug: string, name: string}>} */
        _products: { state: true },
        /** @type {object|null} The post as last saved, or null before the first save. */
        _saved: { state: true },
        _product: { state: true },
        _title: { state: true },
        _body: { state: true },
        _saving: { state: true },
        /** @type {string[]} */
        _errors: { state: true },
        _notice: { state: true },
        _confirmingPublish: { state: true },
        _publishing: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 52rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .body-input {
            min-height: 20rem;
            font-family: ui-monospace, monospace;
            font-size: 0.9rem;
        }
    `;

    constructor() {
        super();
        this.postId = '';
        this._loading = true;
        this._loadError = '';
        this._products = [];
        this._saved = null;
        this._product = '';
        this._title = '';
        this._body = '';
        this._saving = false;
        this._errors = [];
        this._notice = '';
        this._confirmingPublish = false;
        this._publishing = false;
    }

    connectedCallback() {
        super.connectedCallback();
        this._load();
    }

    async _load() {
        this._loading = true;
        this._loadError = '';
        try {
            const productsBody = await api.get('/changelog/products');
            this._products = productsBody.products;

            if (this.postId) {
                // There is no single-post endpoint; the list (drafts included) is
                // small and capped, so find it there.
                const postsBody = await api.get('/changelog/posts');
                const post = postsBody.posts.find((p) => p.id === this.postId);
                if (!post) {
                    this._loadError = 'No such changelog post.';
                    return;
                }
                this._applySaved(post);
            } else {
                this._product = this._products[0]?.slug ?? '';
            }
        } catch (error) {
            this._loadError = error.message;
        } finally {
            this._loading = false;
        }
    }

    /** Adopt a post as returned by the API as both the saved and the edited state. */
    _applySaved(post) {
        this._saved = post;
        this._product = post.product;
        this._title = post.title;
        this._body = post.body;
    }

    get _dirty() {
        return isDirty(this._saved, { title: this._title, body: this._body });
    }

    async _save(event) {
        event.preventDefault();
        this._notice = '';
        this._confirmingPublish = false;

        const { payload, errors } = buildPostPayload({
            title: this._title,
            body: this._body,
            // product is only sent on create; it cannot change afterwards.
            product: this._saved ? undefined : this._product,
        });
        this._errors = errors;
        if (!payload) return;

        this._saving = true;
        try {
            if (this._saved) {
                this._applySaved(await api.put(`/changelog/posts/${this._saved.id}`, payload));
                this._notice = this._saved.published
                    ? 'Saved — the live post is updated.'
                    : 'Draft saved.';
            } else {
                this._applySaved(await api.post('/changelog/posts', payload));
                this._notice = 'Draft saved. It is not on the portal until you publish it.';
                // Give the new post its own URL without re-rendering the view (a
                // router navigation would swap in a fresh editor and drop state).
                window.history.replaceState({}, '', `/changelog/${this._saved.id}`);
            }
        } catch (error) {
            this._errors = [error.message];
        } finally {
            this._saving = false;
        }
    }

    async _publish() {
        this._publishing = true;
        this._errors = [];
        try {
            this._applySaved(await api.post(`/changelog/posts/${this._saved.id}/publish`));
            this._confirmingPublish = false;
            this._notice = 'Published. The post is now live on the community portal.';
        } catch (error) {
            this._errors = [error.message];
        } finally {
            this._publishing = false;
        }
    }

    render() {
        if (this._loading) {
            return html`<div class="wrap"><p class="text-muted">Loading…</p></div>`;
        }
        if (this._loadError) {
            return html`<div class="wrap">
                <div class="alert alert-danger" role="alert">${this._loadError}</div>
                <a href="/changelog">Back to the changelog</a>
            </div>`;
        }

        const status = this._saved ? postStatus(this._saved) : null;
        return html`
            <div class="wrap">
                <a class="small" href="/changelog"><i class="bi bi-arrow-left"></i> Changelog</a>
                <div class="d-flex align-items-center gap-2 mt-2 mb-4">
                    <h1 class="h4 mb-0">${this._saved ? 'Edit post' : 'New post'}</h1>
                    ${
                        status
                            ? html`<span
                                  class="badge ${status === 'published' ? 'text-bg-success' : 'text-bg-warning'}"
                                  >${status}</span
                              >`
                            : nothing
                    }
                </div>

                ${
                    status === 'published'
                        ? html`<div class="alert alert-info" role="status">
                              This post is live. Saving changes updates it immediately.
                          </div>`
                        : nothing
                }

                <form @submit=${this._save}>
                    ${this._renderFields()} ${this._renderMessages()}

                    <div class="d-flex align-items-center gap-2 mt-3">
                        <button class="btn btn-primary" type="submit" ?disabled=${this._saving}>
                            ${this._saving ? 'Saving…' : status === 'published' ? 'Save changes' : 'Save draft'}
                        </button>
                        ${this._renderPublish()}
                    </div>
                </form>
            </div>
        `;
    }

    _renderFields() {
        return html`
            <div class="mb-3">
                <label class="form-label" for="product">Product</label>
                <select
                    id="product"
                    class="form-select"
                    ?disabled=${!!this._saved || this._saving}
                    @change=${(e) => {
                        this._product = e.target.value;
                    }}
                >
                    ${this._products.map(
                        (p) => html`<option value=${p.slug} ?selected=${p.slug === this._product}>
                            ${p.name}
                        </option>`,
                    )}
                </select>
            </div>

            <div class="mb-3">
                <label class="form-label" for="title">Title</label>
                <input
                    id="title"
                    class="form-control"
                    type="text"
                    maxlength=${TITLE_MAX}
                    required
                    .value=${this._title}
                    ?disabled=${this._saving}
                    @input=${(e) => {
                        this._title = e.target.value;
                    }}
                />
            </div>

            <div class="mb-3">
                <label class="form-label" for="body">Body</label>
                <textarea
                    id="body"
                    class="form-control body-input"
                    maxlength=${BODY_MAX}
                    required
                    placeholder="Markdown"
                    .value=${this._body}
                    ?disabled=${this._saving}
                    @input=${(e) => {
                        this._body = e.target.value;
                    }}
                ></textarea>
                <div class="form-text">Markdown.</div>
            </div>
        `;
    }

    _renderMessages() {
        return html`
            ${
                this._errors.length > 0
                    ? html`<div class="alert alert-danger" role="alert">
                          ${this._errors.map((message) => html`<div>${message}</div>`)}
                      </div>`
                    : nothing
            }
            ${
                this._notice
                    ? html`<div class="alert alert-success" role="status">${this._notice}</div>`
                    : nothing
            }
        `;
    }

    _renderPublish() {
        if (!this._saved || this._saved.published) return nothing;

        if (this._confirmingPublish && !this._dirty) {
            return html`
                <span class="ms-auto small">Make this post public?</span>
                <button
                    class="btn btn-danger"
                    type="button"
                    ?disabled=${this._publishing}
                    @click=${this._publish}
                >
                    ${this._publishing ? 'Publishing…' : 'Publish now'}
                </button>
                <button
                    class="btn btn-outline-secondary"
                    type="button"
                    ?disabled=${this._publishing}
                    @click=${() => {
                        this._confirmingPublish = false;
                    }}
                >
                    Cancel
                </button>
            `;
        }

        return html`
            <button
                class="btn btn-outline-danger ms-auto"
                type="button"
                ?disabled=${this._dirty || this._saving}
                title=${this._dirty ? 'Save your changes before publishing' : ''}
                @click=${() => {
                    this._notice = '';
                    this._confirmingPublish = true;
                }}
            >
                Publish…
            </button>
        `;
    }
}

customElements.define('d3-changelog-editor-view', ChangelogEditorView);
